import { useEffect, useMemo, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { useWorkspace } from '../lib/workspace.jsx';
import { api } from '../lib/api.js';
import { duration, number, percent, daysAgo, STATUS_LABEL } from '../lib/format.js';
import OnlinePanel from '../components/OnlinePanel.jsx';

const RANGES = [
  { key: '7d', label: '7d', days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: '90d', label: '90d', days: 90 },
];

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

function Kpi({ label, value, sub, tone }) {
  return (
    <div className="kpi">
      <div className="kpi__label">{label}</div>
      <div className="kpi__value">{value}</div>
      {sub && <div className={`kpi__delta${tone ? ` kpi__delta--${tone}` : ''}`}>{sub}</div>}
    </div>
  );
}

/**
 * Message volume by day, split agent vs visitor — that split is what the
 * aggregation actually returns, so it is what the legend says.
 */
function VolumeChart({ series }) {
  const peak = useMemo(() => Math.max(1, ...series.map((d) => d.total)), [series]);
  const peakDay = useMemo(
    () => series.reduce((best, d) => (d.total > (best?.total ?? -1) ? d : best), null),
    [series]
  );

  // A 90-day range would render unreadably thin bars; label every nth day.
  const step = Math.ceil(series.length / 20);

  return (
    <div className="card">
      <div className="card__head">
        <span className="card__title">Message volume</span>
        <span className="legend">
          <span className="legend__swatch" style={{ background: 'var(--accent)' }} /> Agent
        </span>
        <span className="legend">
          <span className="legend__swatch" style={{ background: 'var(--line2)' }} /> Visitor
        </span>
        <span className="card__meta mono">
          {peakDay ? `peak ${peakDay.total} · ${peakDay.date.slice(5)}` : 'no traffic'}
        </span>
      </div>

      <div className="chart">
        {series.map((day, index) => (
          <div className="chart__col" key={day.date} title={`${day.date}: ${day.total} messages`}>
            <div className="chart__stack">
              <div
                className="chart__bar chart__bar--agent"
                style={{ height: `${(day.agent / peak) * 100}%` }}
              />
              <div
                className="chart__bar chart__bar--visitor"
                style={{ height: `${(day.visitor / peak) * 100}%` }}
              />
            </div>
            <div className="chart__label mono">
              {index % step === 0 ? day.date.slice(8) : ''}
            </div>
          </div>
        ))}
        {series.length === 0 && <div className="table__empty">No messages in this range.</div>}
      </div>
    </div>
  );
}

export function AnalyticsView() {
  const { apps, appIndex, activeAppId, activeApp, allApps } = useWorkspace();
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [perApp, setPerApp] = useState([]);
  const [loading, setLoading] = useState(true);

  const days = RANGES.find((r) => r.key === range).days;

  useEffect(() => {
    // No single app in focus — either none exists yet, or the scope is all of
    // them, where the honest aggregate is the per-app table further down and
    // not a set of headline numbers summed across unrelated tenants.
    if (!activeAppId) {
      setData(null);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);

    const query = { from: daysAgo(days), timezone };

    (async () => {
      try {
        const [overview, messages, firstResponse, resolution, tickets] = await Promise.all([
          api.analytics.overview(activeAppId, query),
          api.analytics.messages(activeAppId, query),
          api.analytics.firstResponse(activeAppId, query),
          api.analytics.resolution(activeAppId, query),
          api.analytics.tickets(activeAppId, query),
        ]);
        if (!cancelled) setData({ overview, messages, firstResponse, resolution, tickets });
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeAppId, days]);

  /* The "by app" table is the same per-tenant endpoint, once per app the
     account can see — there is no cross-tenant aggregate by design. */
  useEffect(() => {
    if (apps.length === 0) return undefined;
    let cancelled = false;
    const query = { from: daysAgo(days), timezone };

    Promise.all(
      apps.map(async (app) => {
        try {
          const overview = await api.analytics.overview(app.id, query);
          return { app, overview };
        } catch {
          return { app, overview: null };
        }
      })
    ).then((rows) => {
      if (!cancelled) setPerApp(rows);
    });

    return () => {
      cancelled = true;
    };
  }, [apps, days]);

  const exportCsv = () => {
    const rows = [
      ['app', 'conversations', 'open', 'messages', 'median_first_response_s', 'avg_resolution_s'],
      ...perApp.map(({ app, overview }) => [
        app.name,
        overview?.conversations.total ?? '',
        overview?.conversations.open ?? '',
        overview?.messages.total ?? '',
        overview?.firstResponse.medianSeconds ?? '',
        overview?.resolution.avgSeconds ?? '',
      ]),
    ];
    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `superchat-analytics-${range}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const overview = data?.overview;

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">Analytics</h1>
        <span className="page__sub">
          {activeApp?.name ?? (allApps ? 'All apps' : 'No app')} · last {days} days ·{' '}
          {timezone}
        </span>
        <div className="page__actions">
          <div className="segmented">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`segmented__item${range === r.key ? ' is-active' : ''}`}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button type="button" className="button" onClick={exportCsv} disabled={perApp.length === 0}>
            <Download size={13} /> Export CSV
          </button>
        </div>
      </header>

      <div className="page__body">
        {/* Presence is live, not part of the range aggregation — so it sits
            above the historical panels and refreshes on its own clock. */}
        <OnlinePanel />

        {loading && (
          <div className="empty">
            <Loader2 size={16} className="spin" /> Crunching aggregates…
          </div>
        )}

        {!loading && !overview && (
          <div className="empty">
            {allApps
              ? 'Headline figures are per app — pick one in the switcher. The by-app table below covers all of them.'
              : 'No analytics available for this app.'}
          </div>
        )}

        {!loading && overview && (
          <>
            <div className="kpis">
              <Kpi
                label="Conversations"
                value={number(overview.conversations.total)}
                sub={`${number(overview.conversations.createdInRange)} new in range`}
              />
              <Kpi
                label="Open now"
                value={number(overview.conversations.open)}
                sub={`${number(overview.conversations.awaitingAgent)} awaiting a reply`}
                tone={overview.conversations.awaitingAgent > 0 ? 'warn' : 'ok'}
              />
              <Kpi label="Messages" value={number(overview.messages.total)} sub="in range" />
              <Kpi
                label="Median first reply"
                value={duration(overview.firstResponse.medianSeconds)}
                sub={`over ${number(overview.firstResponse.count)} replies`}
              />
              <Kpi
                label="Avg resolution"
                value={duration(overview.resolution.avgSeconds)}
                sub={`${number(overview.resolution.count)} resolved`}
              />
              <Kpi
                label="User alerts"
                value={number(overview.conversations.withEmergency)}
                sub={percent(
                  overview.conversations.withEmergency,
                  overview.conversations.total
                )}
                tone={overview.conversations.withEmergency > 0 ? 'danger' : 'ok'}
              />
            </div>

            <VolumeChart series={data.messages.series} />

            <div className="split">
              <div className="card">
                <div className="card__head">
                  <span className="card__title">By app</span>
                  <span className="card__meta">
                    Each tenant queried separately — scope is never merged server-side.
                  </span>
                </div>
                <div className="table table--apps">
                  <div className="table__head">
                    <div>APP</div>
                    <div className="right">CONVS</div>
                    <div className="right">OPEN</div>
                    <div className="right">MESSAGES</div>
                    <div className="right">MED. FIRST REPLY</div>
                    <div className="right">ALERTS</div>
                  </div>
                  <div className="table__body">
                    {perApp.map(({ app, overview: row }) => (
                      <div className="table__row" key={app.id}>
                        <div className="table__app" data-cell="app">
                          <span
                            className="row__stripe"
                            style={{ background: appIndex[app.id]?.color }}
                          />
                          <span className="ellipsis">{app.name}</span>
                        </div>
                        <div className="right mono" data-cell="convs">{number(row?.conversations.total)}</div>
                        <div className="right mono" data-cell="open">{number(row?.conversations.open)}</div>
                        <div className="right mono" data-cell="messages">{number(row?.messages.total)}</div>
                        <div className="right mono" data-cell="reply">
                          {duration(row?.firstResponse.medianSeconds)}
                        </div>
                        <div className="right mono danger" data-cell="alerts">
                          {number(row?.conversations.withEmergency)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card__head">
                  <span className="card__title">Tickets</span>
                  <span className="card__meta mono">
                    {data.tickets.open} open · {data.tickets.closed} closed
                  </span>
                </div>
                <div className="breakdown">
                  {data.tickets.byStatus.map((row) => {
                    const total = data.tickets.open + data.tickets.closed || 1;
                    return (
                      <div className="breakdown__row" key={row.status}>
                        <span className="breakdown__label">
                          {STATUS_LABEL[row.status] ?? row.status}
                        </span>
                        <span className="breakdown__track">
                          <span
                            className="breakdown__fill"
                            style={{ width: `${(row.count / total) * 100}%` }}
                          />
                        </span>
                        <span className="breakdown__value mono">{row.count}</span>
                      </div>
                    );
                  })}
                  {data.tickets.byStatus.length === 0 && (
                    <div className="table__empty">No tickets in this range.</div>
                  )}
                </div>

                <div className="card__head card__head--sub">
                  <span className="card__title">Top events</span>
                </div>
                <div className="breakdown">
                  {overview.topEvents.slice(0, 6).map((event) => (
                    <div className="breakdown__row" key={event.type}>
                      <span className="breakdown__label mono">{event.type}</span>
                      <span className="breakdown__value mono">{number(event.count)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AnalyticsView;
